import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryParams, StringParam } from 'use-query-params';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { CardWrapper } from 'components/CardWrapper';
import { useExecution } from 'hooks/execution';
import { useFetch } from 'services/hooks';
import ExecutionCardStatus from './components/ExecutionCardStatus';
import RiskLevelControl from './components/RiskLevelControl';
import useSetRiskLevelModal from './components/ModalSetRiskLevel';
import Summary from './components/TabsComponent/Summary';
import CompanyInfo from './components/TabsComponent/CompanyInfo';
import Wrapper from '../../wrapper';
import ModalAddresses from './components/TabsComponent/Summary/ModalAddresses';
import People from './components/TabsComponent/People';
import CompanyLink from './components/TabsComponent/CompanyLink';
import FinancialInfo from './components/TabsComponent/FinancialInfo';
import Files from './components/TabsComponent/Files';
import Records from './components/TabsComponent/Records';
import { Header } from './components/Header';
import CustomFetchLoader from './components/CustomFetchLoader';
import useCompanySectionsMapping from './hooks/useCompanySectionsMapping';
import InputData from './components/TabsComponent/InputData';
import './styles.less';

const { TabPane } = Tabs;

const I18N_BASE_PATH = 'pages.private.KYBCompany.pages.companyDetails';

const KYBCompanyDetails = () => {
  const { t: translate } = useTranslation();
  const { transactionId } = useParams();
  const { get: getRecords } = useFetch();
  const {
    loadExecution: getExecution,
    loadingExecution,
    execution,
    reloadExecution,
    refreshExecution,
  } = useExecution();

  const {
    sections: {
      kybCreditReport: creditReport = {},
      kybBusinessIdentity: businessIdentity = {},
    } = {},
  } = execution ?? {};

  const {
    summary: summaryData,
    companyInfo: companyInfoData,
    financial: financialData,
    people: peopleData,
    companyLinks: companyLinksData,
    records: recordsData,
    input: inputData,
  } = useCompanySectionsMapping({
    execution,
    kybCreditReport: creditReport?.data,
    kybBusinessIdentity: businessIdentity?.data,
  });

  const [query, setQuery] = useQueryParams({
    tab: StringParam,
    subTab: StringParam,
  });

  const [riskLevelRecords, setRiskLevelRecords] = useState([]);
  const loadRiskLevelRecords = useCallback(async () => {
    const response = await getRecords({
      url: `/executions/${transactionId}/transaction-records?type=RISK_LEVEL`,
    });

    setRiskLevelRecords(response.records || []);
  }, [getRecords, transactionId]);

  useEffect(() => {
    if (!getExecution) return;
    getExecution({ executionId: transactionId, clearData: true });
    loadRiskLevelRecords();
  }, [transactionId]); // eslint-disable-line

  useEffect(() => {
    if (!reloadExecution) return;
    getExecution({ executionId: transactionId, clearData: true });
    loadRiskLevelRecords();
  }, [reloadExecution]); // eslint-disable-line

  const executionStatus = useMemo(() => execution?.status || 'PROCESSING', [execution]);

  const [newRiskLevel, setNewRiskLevel] = useState(null);
  const riskLevel = useMemo(() => {
    const lastRiskLevel = riskLevelRecords.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];

    return newRiskLevel ?? lastRiskLevel?.data?.riskLevel;
  }, [riskLevelRecords, newRiskLevel]);

  const {
    Modal: AddressesModal,
    modalVisible: addressesModalVisible,
    openModal: openAddressModal,
  } = ModalAddresses();

  const {
    Modal: RiskLevelModal,
    modalVisible: riskLevelModalVisible,
    openModal: handleOpenSetRiskLevelModal,
  } = useSetRiskLevelModal({
    currentRiskLevel: riskLevel,
    transactionId,
    setNewRiskLevel,
  });

  const riskLevelControl = useMemo(
    () => (
      <RiskLevelControl onOpenModal={handleOpenSetRiskLevelModal} riskLevel={riskLevel} />
    ),
    [riskLevel, handleOpenSetRiskLevelModal]
  );

  // When changing tabs was re-render this div, restarting the countdown, useMemo solve the problem
  const rightSide = useMemo(() => {
    return (
      <aside className="kyb-company-details-right-side">
        <CardWrapper hasPadding className="remove-bottom-top remove-min-height">
          <ExecutionCardStatus type={executionStatus} />
          {riskLevelControl}
        </CardWrapper>
      </aside>
    );
  }, [riskLevelControl, executionStatus]);

  return (
    <>
      {addressesModalVisible ? AddressesModal : <></>}
      {riskLevelModalVisible ? RiskLevelModal : <></>}
      <Wrapper id="KYB-company-details-wrapper">
        {(loadingExecution && !execution) || execution?.status === 'PROCESSING' ? (
          <>
            <CustomFetchLoader
              refreshExecution={refreshExecution}
              message={translate(
                `${I18N_BASE_PATH}.loadingMessage`,
                'The report is being collated, please wait a moment...'
              )}
            />
          </>
        ) : (
          <section className="kyb-company-details-wrapper">
            <aside className="kyb-company-details-left-side">
              <CardWrapper hasPadding className="remove-bottom-top">
                <Header
                  createdAt={execution?.createdAt}
                  updatedAt={execution?.updatedAt}
                  companyData={{
                    transactionId,
                    executionStatus,
                    riskLevel,
                    summary: summaryData,
                    records: recordsData,
                    companyLinks: companyLinksData,
                    people: peopleData,
                    financial: financialData,
                    companyInfo: companyInfoData,
                    inputData,
                  }}
                />
                <Tabs
                  className="company-details-tabs-wrapper"
                  defaultActiveKey="summary"
                  activeKey={query.tab}
                  onChange={(tab) => setQuery({ tab, subTab: undefined })}
                  destroyInactiveTabPane
                >
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.summary`, 'Summary')}
                    key="summary"
                  >
                    <Summary
                      openAddressModal={openAddressModal}
                      summaryData={summaryData}
                    />
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.companyInfo`, 'Company info')}
                    key="companyInfo"
                  >
                    <CompanyInfo
                      companyInfoData={companyInfoData}
                      openAddressModal={openAddressModal}
                    />
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.financial`, 'Financial info')}
                    key="financial"
                  >
                    <FinancialInfo financialData={financialData} />
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.people`, 'People')}
                    key="people"
                  >
                    <People
                      query={query}
                      setQuery={setQuery}
                      peopleData={peopleData}
                      summaryData={summaryData}
                    />
                  </TabPane>
                  <TabPane
                    tab={translate(
                      `${I18N_BASE_PATH}.tabs.companyLinks`,
                      'Company links'
                    )}
                    key="companyLinks"
                  >
                    <CompanyLink
                      query={query}
                      setQuery={setQuery}
                      companyLinksData={companyLinksData}
                    />
                    <h1>
                      {translate(`${I18N_BASE_PATH}.tabs.companyLinks`, 'Company links')}
                    </h1>
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.records`, 'Records')}
                    key="records"
                  >
                    <Records transactionId={transactionId} data={recordsData} />
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.files`, 'Files')}
                    key="files"
                  >
                    <Files transactionId={transactionId} />
                  </TabPane>
                  <TabPane
                    tab={translate(`${I18N_BASE_PATH}.tabs.inputData`, `Input Data`)}
                    key="inputData"
                  >
                    <InputData inputData={inputData} />
                  </TabPane>
                </Tabs>
              </CardWrapper>
            </aside>
            {rightSide}
          </section>
        )}
      </Wrapper>
    </>
  );
};

export default KYBCompanyDetails;
